package com.dotdrives.client

import com.dotdrives.config.BuildKonfig
import com.dotdrives.model.*
import io.ktor.client.HttpClient
import io.ktor.client.request.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.*
import kotlinx.serialization.json.*

/** Intentionally anonymous: no account bootstrap, user JWT, or staff table reads. */
class PublicGivingClient(
    private val baseUrl: String = BuildKonfig.SUPABASE_URL,
    private val publicKey: String = BuildKonfig.SUPABASE_PUBLISHABLE_KEY,
    private val httpFactory: () -> HttpClient = { HttpClient() },
) {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    suspend fun form(orgId: String, slug: String): PublicGivingForm =
        json.decodeFromJsonElement(
            request(
                buildJsonObject {
                    put("action", "form")
                    put("org_id", orgId)
                    put("public_slug", slug)
                }
            )
        )

    suspend fun checkout(input: PublicCheckoutRequest): PublicCheckoutResult =
        json.decodeFromJsonElement(request(json.encodeToJsonElement(input)))

    suspend fun restartCheck(input: PublicCheckoutRequest): PublicRestartResult =
        json.decodeFromJsonElement(
            request(
                buildJsonObject {
                    put("action", "restart_check")
                    put("checkout_request", json.encodeToJsonElement(input))
                }
            )
        )

    suspend fun status(orgId: String, operationId: String, token: String): PublicPaymentStatus =
        json.decodeFromJsonElement(
            request(
                buildJsonObject {
                    put("action", "status")
                    put("org_id", orgId)
                    put("operation_id", operationId)
                    put("status_token", token)
                }
            )
        )

    private suspend fun request(body: JsonElement): JsonElement {
        val http = httpFactory()
        try {
            val response =
                http.post("${baseUrl.trimEnd('/')}/functions/v1/stripe-checkout") {
                    header("apikey", publicKey)
                    contentType(ContentType.Application.Json)
                    setBody(body.toString())
                }
            val result = json.parseToJsonElement(response.bodyAsText()).jsonObject
            if (!response.status.isSuccess()) {
                val code = result["error"]?.jsonObject?.get("code")?.jsonPrimitive?.content
                throw PublicGivingException(
                    code ?: "unavailable",
                    when (code) {
                        "service_unavailable",
                        "payments_not_ready",
                        "payment_unavailable" ->
                            "Online giving is not available yet. Please contact the organization or try again later."
                        "conflict",
                        "payment_conflict" ->
                            "This gift or form has changed. Check the original payment status before starting another gift."
                        "reconciliation_required" ->
                            "This payment needs review. Please contact the organization before trying again."
                        "invalid_form_configuration" ->
                            "This form needs an update. Please contact the organization about its contact fields or another way to give."
                        "invalid_request" ->
                            "This form is unavailable or the gift details need attention. Please check the link and your entries."
                        else -> "We couldn’t complete the request. Please try again."
                    },
                )
            }
            return result["data"] ?: error("The giving service returned an incomplete response.")
        } finally {
            http.close()
        }
    }
}

class PublicGivingException(val code: String, message: String) : Exception(message)
